import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IntlProvider } from "react-intl";
import { Header } from "./Header";
import { Main } from "./Main";
import { TopLevelLayout } from "../components/layout/TopLevelLayout";
import { fetchPlacements } from "../store/placements";
import { setConfig } from "../store/config";
import { RootState } from "../store";
import { setResults } from "../store/results";
import { setDistanceToPlacementById } from "../store/distance";
import { useLocaleMessages } from "../hooks/useLocaleMessages";
import { usePlacementResults } from "../hooks/usePlacementResults";
import { calculateDistanceToPlacementById } from "../services/distance";
import { useSyncRouteToRedux } from "../hooks/useSyncRouteToRedux";
import { UIConfig } from "../types/UIConfig";
import { Analytics } from "./Analytics";
import { setOrganization } from "../store/organization";
import { Organization } from "../types/Organization";

interface AppProps {
  initialConfig: UIConfig;
  organization: Organization;
}

export const App = ({
  initialConfig,
  organization,
}: AppProps): React.ReactElement | null => {
  useSyncRouteToRedux();
  const placements = useSelector((state: RootState) => state.placements);
  const config = useSelector((state: RootState) => state.config);
  const results = useSelector((state: RootState) => state.results);
  const address = useSelector((state: RootState) => state.address);
  const lang = useSelector((state: RootState) => state.lang);
  const placementResults = usePlacementResults();
  const messages = useLocaleMessages(lang.locale);
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(setOrganization(organization));
  }, [dispatch, organization]);

  React.useEffect(() => {
    dispatch(setConfig(initialConfig));
  }, [dispatch, initialConfig]);

  React.useEffect(() => {
    dispatch(
      fetchPlacements({
        placementMapping: initialConfig.placement,
        lang: lang.locale,
        organizationPath: organization.path,
        eligibilityConfig: initialConfig.eligibility,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig, lang, organization]);

  const placementIds = React.useMemo(() => {
    if (placements.status !== "finished") return [];
    return Object.keys(placements.byId);
  }, [placements]);

  React.useEffect(() => {
    if (placements.status !== "finished") return;
    dispatch(setResults(placementIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, placementIds.length, placements.status]);

  React.useEffect(() => {
    if (results.status !== "finished" || placementResults === null) return;
    dispatch(setResults(placementResults.map((p) => p.id)));
  }, [dispatch, placementResults, results.status]);

  React.useEffect(() => {
    if (
      placements.status === "finished" &&
      address.status === "finished" &&
      config.state === "finished"
    ) {
      const distanceToPlacementById = calculateDistanceToPlacementById(
        address.values,
        placements.byId,
        config.values
      );
      dispatch(setDistanceToPlacementById(distanceToPlacementById));
    }
  }, [dispatch, placements, address, config]);

  let icon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");

  React.useEffect(() => {
    (async () => {
      if (config.state === "finished" && config.values.faviconUrl && icon) {
        const response = await fetch(config.values.faviconUrl);

        if (response.ok) icon.href = config.values.faviconUrl;
      }
    })();
  }, [config, icon]);

  const descriptionMeta = document.querySelector<HTMLMetaElement>(
    "meta[name='description']"
  );
  const ogTitleMeta = document.querySelector<HTMLMetaElement>(
    "meta[property='og:title']"
  );
  const ogDescriptionMeta = document.querySelector<HTMLMetaElement>(
    "meta[property='og:description']"
  );

  if (descriptionMeta)
    descriptionMeta.setAttribute("content", organization.title);
  if (ogTitleMeta) ogTitleMeta.setAttribute("content", organization.title);
  if (ogDescriptionMeta)
    ogDescriptionMeta.setAttribute("content", organization.title);

  React.useEffect(() => {
    document.title = organization.title;
  }, [organization]);

  return (
    <IntlProvider locale={lang.locale} messages={messages}>
      <TopLevelLayout header={<Header />} main={<Main />} />
      <Analytics />
    </IntlProvider>
  );
};
